import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Show "Showing X–Y of Z" label (default true) */
  showRange?: boolean;
  prevTestId?: string;
  nextTestId?: string;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  showRange = true,
  prevTestId,
  nextTestId,
}: PaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between gap-2 pt-4">
      {showRange ? (
        <p className="text-base text-muted-foreground">
          Showing {page * pageSize + 1}&ndash;
          {Math.min((page + 1) * pageSize, total)} of {total}
        </p>
      ) : (
        <div />
      )}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          aria-label="Previous page"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          data-testid={prevTestId}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-base text-muted-foreground">
          Page {page + 1} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          aria-label="Next page"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          data-testid={nextTestId}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
